import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import SectionHeader from '../../../components/SectionHeader';
import Button from '../../../components/buttons/Button';
import { useCallback, useRef } from 'react';
import { scrollToError } from '../../../utils/HelperFunctions';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ILead } from '../types/interfaces';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';
import useModelSelectOptions from '../../../hooks/useModelSelectOptions';
import { customSelectStyles } from '../../../utils/SelectStyles';
import InputSkeleton from '../../../components/loaders/InputSkeleton';

const CreateLeadForm = ({ setAddLead, setLeadId }: { setAddLead?: React.Dispatch<React.SetStateAction<boolean>>; setLeadId?: (id: number) => void }) => {
  const { id } = useParams();
  const formRef = useRef(null);
  const { t } = useTranslation();

  const { apiData, getDataLoading, handleSubmit, submitLoading } = useFormsIntegrationHelpers({
    queryKey: ['leads', id],
    invalidateQueryKey: ['leads'],
    id,
    singleGetApi: `/admin/leads/${id}`,
    addApi: '/admin/leads',
    editApi: `/admin/leads/${id}`,
    listRoute: setAddLead ? undefined : '/leads',
    itemName: t('lead'),
    onComplete: (_, res) => {
      setAddLead && setAddLead(false);
      setLeadId && setLeadId(res?.data?.data?.id);
    }
  });

  const beforeSalesOptionsMaping = useCallback((options: any) => {
    return options.filter((option: any) => option?.roles?.some((role: any) => role?.permissions?.some((permission: any) => permission.name === 'leeds.leads_can_be_assigned')));
  }, []);

  const { modelOptions: salesOptions, apiDataLoading: salesLoading } = useModelSelectOptions({
    labelKey: ['first_name', 'last_name'],
    valueKey: 'id',
    withoutDashes: true,
    beforeOptionsMaping: beforeSalesOptionsMaping,
    modelRoute: 'admin/admins?relations=roles,roles.permissions',
    queryKey: ['admins']
  });

  const validationSchema = Yup.object({
    first_name: Yup.string().required(t('required')),
    last_name: Yup.string().required(t('required')),
    email: Yup.string()
      .email('Enter valid email format')
      .required(t('required'))
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Please remove spaces'),
    mobile: Yup.string()
      .required(t('required'))
      .matches(/^\+?[0-9]{7,15}$/, t('wrong_number'))
  });

  const initialValues: ILead = {
    ...apiData,
    
    first_name: apiData?.first_name || '',
    last_name: apiData?.last_name || '',
    email: apiData?.email || '',
    mobile: apiData?.mobile || '',
    provider_lead_id: apiData?.provider_lead_id || '',
    
    sales_id: apiData?.assigned_sales?.id || ''
  };

  if (getDataLoading)
    return (
      <FormDynamicSkeleton
        sections={[
          [
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2 }
          ]
        ]}
        buttons={1}
      />
    );

  return (
    <div className={'form_section'} ref={formRef}>
      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setErrors }) => {
          let value = { ...values, _method: 'POST' };
          if (id) {
            value = { ...value, _method: 'PUT' };
          }
          handleSubmit({ ...value }, setErrors);
        }}
      >
        {(formik) => (
          <>
            <Form>
              <SectionHeader title={t('lead_details')} customStyle="last">
                <div className="inputs_group">
                  <FieldWrapper title={t('first_name')} inputName={`first_name`} inputPlaceholder={t('first_name')} input customPadding />
                  <FieldWrapper title={t('last_name')} inputName={`last_name`} inputPlaceholder={t('last_name')} input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('email')} inputName={`email`} inputPlaceholder={t('email')} input customPadding />
                  <FieldWrapper title={t('phone')} inputName={`mobile`} inputPlaceholder={t('phone')} input customPadding />
                </div>
                <div className="inputs_group">
                  {salesLoading ? (
                    <InputSkeleton />
                  ) : (
                    <FieldWrapper
                      customPadding
                      title={t('sales')}
                      inputPlaceholder={t('sales')}
                      inputName={`sales_id`}
                      options={salesOptions}
                      selectStyle={customSelectStyles}
                      select
                    />
                  )}
                  <FieldWrapper title={t('provider_lead_id')} inputName={`provider_lead_id`} inputPlaceholder={t('provider_lead_id')} input customPadding />
                </div>
              </SectionHeader>
              <div className="form_button reverse">
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  {setAddLead && (
                    <Button
                      type="button"
                      onClick={() => {
                        setAddLead(false);
                      }}
                    >
                      <span className="bold">{t('cancel')}</span>
                    </Button>
                  )}
                  <Button
                    loading={submitLoading}
                    type="button"
                    onClick={() => {
                      formik?.submitForm();
                      scrollToError(!formik.isValid, formRef);
                    }}
                  >
                    <span className="bold">{t('save')}</span>
                  </Button>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateLeadForm;
